import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'src/components/button/Button';
import { Collection } from 'src/components/common/cards/types/types';
import Countdown from 'src/components/common/Countdown';
import IconSvg, { Icons } from 'src/components/common/ui/IconSvg';
import ProgressBar from 'src/components/common/ui/ProgressBar';
import SyncLoader from "react-spinners/SyncLoader";

interface Props {
  arenaTotalMintedKeys: string;
  collection: Collection;
  handleClick: (collection: Collection, buttonType: 'mint' | 'openbox' | 'checkReward') => void;
}

export default function MintTableRow({ collection, handleClick, arenaTotalMintedKeys }: Props) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const navigate = useNavigate();
  const getIconName = (networkCurrency: string): Icons => {
    switch (networkCurrency) {
      case 'AVAX':
        return 'avaxM';
      case 'ETH':
        return 'ethM';
      case 'BNB':
        return 'bnbM';
      case 'SOL':
        return 'solM';
      case 'EGLD':
        return 'egld';
      case 'SUI':
        return 'suiM';
      default:
        return 'ethM'; // Default icon if currency is not listed
    }
  };

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen); // Toggle the accordion state
  };

  const iconName = getIconName(collection.network.networkCurrency);

  // const startingSoon = collection?.stages[0]?.isSoon;

  const isBlurred = !collection?.isEarnBlured;
  return (
    <>
      <div
        onClick={!collection?.isEarnBlured ? toggleAccordion : undefined}
        className={`grid grid-cols-15 cursor-pointer px-5 py-[17.5px] rounded-md mb-2.5 last-of-type:mb-0 items-center ${
          isAccordionOpen ? 'bg-grayscale-900 border-2 border-grayscale-700' : 'bg-grayscale-800'
        }`}
      >
        <div className="flex items-center col-span-4 text-sm gap-x-5 text-grayscale-300">
          <img
            src={
              isBlurred
                ? collection.logo
                : `/assets/images/blur-logo-${Math.floor(Math.random() * 4) + 1}.png`
            }
            className="w-[60px] h-[60px] bg-cover bg-center"
          />

          <h5 className="text-brand-primary-light max-w-[255px] rounded-[5px] pr-2.5">
            {isBlurred ? (
              collection.name
            ) : (
              <img
                src={`/assets/images/blur-collection-name-${Math.floor(Math.random() * 2) + 1}.png`}
                alt="blur collection name"
              />
            )}
          </h5>
        </div>

        <div className="col-span-2">
          {isBlurred ? (
            <>
              <p className="text-base font-semibold 2xl:text-lg text-grayscale-100">
                ≈ ${Number(collection.rewardsTotalValue).toLocaleString()}
              </p>
            </>
          ) : (
            <img src={'/assets/images/blur-collection-name-sm.png'} alt="blur price" />
          )}
        </div>

        <div className="flex items-center col-span-2 space-x-2">
          {isBlurred ? (
            <>
              <IconSvg icon={iconName} />

              <p className="font-normal text-xss text-grayscale-300">
                {collection.network.networkName}
              </p>
            </>
          ) : (
            <img src={'/assets/images/blur-price.png'} alt="blur price" className="w-[110px]" />
          )}
        </div>

        <div className="col-span-3 text-lg font-bold text-start text-grayscale-100">
          {collection?.status === 'Live' && isBlurred && '-'}
        </div>

        <div className="col-span-2 text-grayscale-300">
          {(() => {
            if (collection?.stages[0]?.isExpired) {
              // Expired project
              return (
                <div className="py-1 px-3 leading-none rounded-md bg-system-error-dark border-system-error flex gap-1.5 items-center border w-fit">
                  <IconSvg icon="expired" />

                  <span className="text-xs text-grayscale-100 2xl:text-sm">Expired</span>
                </div>
              );
            } else if (collection?.stages[0]?.isLive) {
              // Live project
              return (
                <div className="py-1 px-3 leading-none rounded-md bg-system-success-dark border-system-success flex gap-1.5 items-center border w-fit">
                  <IconSvg icon="live2M" />
                  <span className="text-xs text-grayscale-100 2xl:text-sm">Live</span>
                </div>
              );
            } else {
              // Soon project
              return (
                <div className="py-1 px-3 leading-none rounded-md bg-system-warning-dark border-system-warning flex gap-1.5 items-center border w-fit">
                  <IconSvg icon="comingSoon2M" />
                  <span className="text-xs text-grayscale-100 2xl:text-sm">Soon</span>
                </div>
              );
            }
          })()}
        </div>

        <div className="flex items-center justify-end col-span-2">
          {isBlurred ? (
            <button
              className={`flex items-center p-2 rounded-md gap-x-2 justify-center w-[120px] 2xl:w-[153px] text-xs 2xl:text-base ${
                isAccordionOpen
                  ? 'bg-brand-primary-light text-grayscale-800'
                  : 'bg-grayscale-700 text-grayscale-200'
              }`}
            >
              <span>{isAccordionOpen ? 'Show less' : 'Show all'}</span>

              <IconSvg
                icon="arrowDownM"
                className={`transition-transform duration-300 ${
                  isAccordionOpen ? 'rotate-180 text-grayscale-800' : 'text-brand-primary-light'
                }`}
              />
            </button>
          ) : (
            <img src={'/assets/images/blur-button.png'} alt="blur button" />
          )}
        </div>
      </div>

      {isAccordionOpen && (
        <>
          {collection.stages
            .filter(st => st?.isShow)
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((stage, index) => (
              <div
                key={index}
                className="grid grid-cols-15 px-5 py-[17.5px] rounded-md bg-grayscale-900 mb-2.5 last-of-type:mb-0 items-center border-2 border-grayscale-700"
              >
                <div className="flex items-center col-span-4 text-sm gap-x-5 text-grayscale-300">
                  <img src={collection.logo} className="w-[60px] h-[60px]" />

                  <h5 className="text-brand-primary-light max-w-[255px] capitalize">
                    {stage.stageName}
                  </h5>
                </div>

                <div className="col-span-2">
                  <p className="text-base font-semibold 2xl:text-lg text-grayscale-100">
                    ≈ ${Number(collection.rewardsTotalValue).toLocaleString()}
                  </p>
                </div>

                <div className="col-span-2 flex items-center gap-[9px]">
                  {/* <p className="text-base font-semibold 2xl:text-lg text-grayscale-100">
                    {collection.network.networkName}
                  </p> */}

                  {/* <IconSvg icon={iconName} />

                <p className="text-lg font-semibold text-grayscale-100">0,000</p> */}
                </div>

                <div className="col-span-3 pr-5 max-w-[300px]">
                  {collection.slug !== 'multiversx' && (
                    <>
                      {arenaTotalMintedKeys !== '' ? (
                        <>
                          {stage.isLive && (
                            <ProgressBar
                              progress={Math.floor((Number(arenaTotalMintedKeys) / 1629) * 100)}
                              size="Full"
                              estimatedValue={Number(arenaTotalMintedKeys)}
                              totalValue="1629"
                            />
                          )}
                        </>
                      ) : (
                        <SyncLoader color="#B73FFF" size={5} />
                      )}
                    </>
                  )}
                </div>

                <div className="col-span-2 text-sm text-grayscale-300">
                  {(() => {
                    if (stage.isExpired) {
                      // Expired project
                      return (
                        <div className="py-1 px-3 leading-none rounded-md bg-system-error-dark border-system-error flex gap-1.5 items-center border w-fit">
                          <IconSvg icon="expired" />

                          <span className="text-sm text-grayscale-100">Expired</span>
                        </div>
                      );
                    } else if (stage.isLive) {
                      // Live project
                      return (
                        <div className="py-1 px-3 leading-none rounded-md bg-system-success-dark border-system-success flex gap-1.5 items-center border w-fit">
                          <IconSvg icon="live2M" />
                          <span className="text-sm text-grayscale-100">Live</span>
                        </div>
                      );
                    } else {
                      // Soon project
                      return (
                        <div className="py-1 px-3 leading-none rounded-md bg-system-warning-dark border-system-warning flex gap-1.5 items-center border w-fit">
                          <IconSvg icon="comingSoon2M" />
                          <span className="text-sm text-grayscale-100">Soon</span>
                        </div>
                      );
                    }
                  })()}

                  {stage && collection.slug !== 'multiversx' && (
                    <div className="mt-3 text-sm font-semibold text-grayscale-200 ">
                      <Countdown stage={stage} />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end col-span-2">
                  {/* {(() => {
                  if (stage.isExpired) {
                    // Expired project
                    return (
                      <Button type="ghost" size="m" onClick={() => handleClick(collection, 'openbox')}>
                        Open Box
                      </Button>
                    );
                  } else if (collection.name !== 'Qiibee' && stage.isLive) {
                    // Live project
                    return (
                      <Button type="primary" size="m" onClick={() => handleClick(collection, 'mint')}>
                        Mint
                      </Button>
                    );
                  } else if (collection.name == 'Qiibee' && stage.isLive) {
                    // Live project
                    return (
                      <Button type="ghost" size="m">
                        Deposit
                      </Button>
                    );
                  } else {
                    // Soon project
                    return (
                      <Button type="secondary" size="m" onClick={() => handleClick(collection, 'mint')}>
                        Join
                      </Button>
                    );
                  }
                })()} */}
                  {stage?.isLive && (
                    <div className="flex items-center justify-end col-span-2">
                      <Button
                        onClick={() =>
                          collection.slug === 'multiversx'
                            ? navigate('/multiversx/leaderboard/multiversx')
                            : navigate(`/arena/${collection.id}/${stage?.id}`)
                        }
                        type="primary"
                        size="xs"
                        classNames="!min-w-[74px]"
                      >
                        {collection.slug === 'multiversx' ? 'Earn' : 'Claim'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </>
      )}
    </>
  );
}
